import {
  NATIVE_TOKEN_ADDRESS_PER_CHAIN_ID,
  NATIVE_TOKEN_DATA_ADDRESSES,
} from '@/constants/addresses';
import { CHAIN_IDS } from '@/constants/chain';

import { useDedaubTokenCheck } from '@/hooks/useDedaubTokenCheck';
import { useSolanaTokenSecurityInfo } from '@/hooks/useSolanaTokenSecurity';
import { useTokenInfo } from '@/hooks/useTokenInfo';

import { TokenResult } from '@/utils/0x/token-registry.types';

import type { BadgeVariant } from '../components/TokenProfile/TokenProfileSecurityAudit/RiskIcons/BadgeIcon';
import {
  type SecurityAlertsResult,
  type SecurityLevel,
  calculateSecurityAlerts,
  calculateSolanaSecurityAlerts,
  getEvmSecurityScore,
  getSolanaSecurityLevel,
} from '../components/TokenProfile/TokenProfileSecurityAudit/utils';

interface Params {
  activeToken?: TokenResult;
}

interface SecurityAssessmentResult {
  includesDedaubScan: boolean;
  isDedaubScanLoading: boolean;
  level: SecurityLevel;
  risks: number;
  /** Always 0 on Solana (no high-risk tier). */
  highRisks: number;
  alerts: SecurityAlertsResult;
}

/**
 * Shared security-assessment data hook. Fetches CoinGecko listing info, then
 * branches between the EVM and Solana scorers. `getSecurityLabel` derives the
 * human-readable summary from the resulting level + risk count.
 */
export const useSecurityAssessment = ({ activeToken }: Params): SecurityAssessmentResult => {
  const {
    data: dedaubScan,
    isFeatureEnabled: includesDedaubScan,
    isLoading,
  } = useDedaubTokenCheck({
    address: activeToken?.address,
    chainId: activeToken?.chainId,
  });
  const isDedaubScanLoading = includesDedaubScan && isLoading;
  const { data: tokenInfo } = useTokenInfo(activeToken?.address, activeToken?.chainId);
  const { data: solanaSecurityData } = useSolanaTokenSecurityInfo({
    tokenAddress: activeToken?.chainId === CHAIN_IDS.SOLANA ? activeToken?.address : undefined,
  });

  const isListedOnCoinGecko = Boolean(tokenInfo?.info?.id);
  const isListedOnCoinbaseOrBinance =
    tokenInfo?.info?.tickers?.filter((ticker) => ticker.market?.name === 'Coinbase Exchange')[0]
      ?.trade_url !== undefined ||
    tokenInfo?.info?.tickers?.filter((ticker) => ticker.market?.name === 'Binance')[0]
      ?.trade_url !== undefined;

  if (!activeToken) {
    return {
      includesDedaubScan,
      isDedaubScanLoading,
      level: 'Pending',
      risks: 0,
      highRisks: 0,
      alerts: {
        externalDependencies: [],
        accessControls: [],
        issuanceInformation: [],
        customBehaviors: [],
        previewAlerts: [],
      },
    };
  }

  // Canonical chain assets (native + its ERC-20 counterpart: wrapped native, or
  // USDC on Arc) short-circuit to Safe. These are well-known low-risk assets
  // where the underlying scoring data is either missing (natives) or noisy
  // (e.g. WETH/WBNB flagged as mintable). MAT-4301: Mantle's native MNT is
  // excluded because token-api overrides its values with real risk signals.
  const isCanonicalChainAsset =
    activeToken.address === NATIVE_TOKEN_DATA_ADDRESSES[activeToken.chainId] ||
    (activeToken.chainId !== CHAIN_IDS.MANTLE &&
      activeToken.address === NATIVE_TOKEN_ADDRESS_PER_CHAIN_ID[activeToken.chainId]);

  if (isCanonicalChainAsset) {
    return {
      includesDedaubScan,
      isDedaubScanLoading,
      level: 'Safe',
      risks: 0,
      highRisks: 0,
      alerts:
        activeToken.chainId === CHAIN_IDS.SOLANA
          ? calculateSolanaSecurityAlerts(solanaSecurityData)
          : calculateSecurityAlerts(activeToken, includesDedaubScan, dedaubScan),
    };
  }

  if (activeToken.chainId === CHAIN_IDS.SOLANA) {
    if (!solanaSecurityData) {
      return {
        includesDedaubScan,
        isDedaubScanLoading,
        level: 'Pending',
        risks: 0,
        highRisks: 0,
        alerts: calculateSolanaSecurityAlerts(undefined),
      };
    }
    const { level, risks } = getSolanaSecurityLevel(
      solanaSecurityData,
      isListedOnCoinbaseOrBinance,
      isListedOnCoinGecko,
    );
    return {
      includesDedaubScan,
      isDedaubScanLoading,
      level,
      risks,
      highRisks: 0,
      alerts: calculateSolanaSecurityAlerts(solanaSecurityData),
    };
  }

  const score = getEvmSecurityScore(
    activeToken,
    isListedOnCoinbaseOrBinance,
    isListedOnCoinGecko,
    includesDedaubScan,
    dedaubScan,
  );
  return {
    ...score,
    includesDedaubScan,
    isDedaubScanLoading,
    level: includesDedaubScan && isLoading && score.level !== 'Danger' ? 'Pending' : score.level,
    alerts: calculateSecurityAlerts(activeToken, includesDedaubScan, dedaubScan),
  };
};

/** Short summary text shown on the score badge ("Pending", "Danger", or "N risk(s) detected"). */
export const getSecurityLabel = (level: SecurityLevel, riskCount: number): string => {
  if (level === 'Pending') return 'Pending';
  if (level === 'Danger') return 'Danger';
  return `${riskCount} ${riskCount === 1 ? 'risk' : 'risks'} detected`;
};

export const SECURITY_LEVEL_TO_BADGE_VARIANT: Record<SecurityLevel, BadgeVariant> = {
  Neutral: 'neutral',
  Risky: 'risky',
  Danger: 'danger',
  Pending: 'pending',
  Safe: 'safe',
};
